import type {
  StoretextOverallAnalysis,
  UnifiedDetailedAnalysis,
} from "@a7y/types/aso";

type ScoreBreakdown = NonNullable<UnifiedDetailedAnalysis["score_breakdown"]>;

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function scoreFromBreakdown(
  scoreBreakdown: ScoreBreakdown | undefined,
  key: string,
): number | null {
  return finiteNumber(scoreBreakdown?.[key]?.score);
}

function deriveOverallScore(
  characterUtilizationScore: number | null,
  fieldOptimizationScore: number | null,
  contentQualityScore: number | null,
  crossFieldScore: number | null,
): number | null {
  if (
    characterUtilizationScore == null ||
    fieldOptimizationScore == null ||
    contentQualityScore == null
  ) {
    return null;
  }

  if (crossFieldScore != null) {
    return Math.round(
      characterUtilizationScore * 0.3 +
        fieldOptimizationScore * 0.35 +
        contentQualityScore * 0.2 +
        crossFieldScore * 0.15,
    );
  }

  return Math.round(
    characterUtilizationScore * 0.3 +
      fieldOptimizationScore * 0.4 +
      contentQualityScore * 0.3,
  );
}

function resolveOverallScore(candidates: Array<number | null>): number {
  const numericCandidates = candidates.filter((score): score is number => score != null);
  return numericCandidates[0] ?? 0;
}

export function buildStoretextOverallForDisplay(
  overallAnalysis: Partial<StoretextOverallAnalysis>,
  detail: UnifiedDetailedAnalysis,
): StoretextOverallAnalysis {
  const scoreBreakdown = detail.score_breakdown;
  const characterUtilizationScore =
    scoreFromBreakdown(scoreBreakdown, "character_utilization") ??
    finiteNumber(overallAnalysis.character_utilization_score) ??
    0;
  const fieldOptimizationScore =
    scoreFromBreakdown(scoreBreakdown, "field_optimization") ??
    finiteNumber(overallAnalysis.field_optimization_score) ??
    0;
  const contentQualityScore =
    scoreFromBreakdown(scoreBreakdown, "content_quality") ??
    finiteNumber(overallAnalysis.content_quality_score) ??
    0;
  const crossFieldScore =
    scoreFromBreakdown(scoreBreakdown, "cross_field") ??
    finiteNumber(overallAnalysis.cross_field_score);
  const derivedOverallScore = deriveOverallScore(
    characterUtilizationScore,
    fieldOptimizationScore,
    contentQualityScore,
    crossFieldScore,
  );

  return {
    ...overallAnalysis,
    character_utilization_score: characterUtilizationScore,
    character_utilization_status:
      overallAnalysis.character_utilization_status ?? "sufficient",
    field_optimization_score: fieldOptimizationScore,
    content_quality_score: contentQualityScore,
    cross_field_score: crossFieldScore,
    overall_score: resolveOverallScore([
      finiteNumber(overallAnalysis.overall_score),
      scoreFromBreakdown(scoreBreakdown, "overall"),
      scoreFromBreakdown(scoreBreakdown, "overall_score"),
      derivedOverallScore,
    ]),
    cross_field_opportunities: overallAnalysis.cross_field_opportunities ?? [],
    key_insights: overallAnalysis.key_insights ?? [],
    priority_actions: overallAnalysis.priority_actions ?? [],
  };
}

